import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../utils/apiHelper';
import { showServerError, showError, showWarning } from '../../utils/toastHelper';


/**
 * A hook to manage data fetching, pagination, sorting, and filtering for admin tables
 * @param {string} endpoint - API endpoint to fetch data from (e.g., '/users')
 * @param {Object} options - Initial configuration options
 */
export const useDataTable = (endpoint, options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: options.limit || 10,
    total: 0,
    totalPages: 0,
    ...options.initialPagination
  });

  const [filters, setFilters] = useState(options.initialFilters || {});
  const [sort, setSort] = useState(options.initialSort || { field: 'createdAt', order: 'desc' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();

    // Pagination
    params.append('page', pagination.page);
    params.append('limit', pagination.limit);

    // Sorting
    if (sort.field) {
      params.append('sortBy', sort.field);
      params.append('sortOrder', sort.order);
    }

    // Filters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== '' && filters[key] !== 'all') {
        params.append(key, filters[key]);
      }
    });

    return params.toString();
  }, [pagination.page, pagination.limit, sort, filters]);

  const fetchData = useCallback(async () => {
    if (!endpoint) return;

    try {
      setLoading(true);
      setError(null);

      const queryString = buildQueryString();
      const response = await apiRequest(`${endpoint}?${queryString}`);

      if (response.success) {
        // Handle different response structures gracefully
        const listData = response.data || response.users || response.orders || response.products || response.companies || response.subscriptions || response.discountCodes || response.carriers || [];
        const meta = response.pagination || response.meta || {};

        const currentLimit = pagination.limit;
        const newTotalPages = meta.totalPages || meta.total_pages || Math.ceil((meta.total || 0) / currentLimit) || 1;

        // Auto-correct pagination if we deleted the last items on the current page
        if (pagination.page > newTotalPages && newTotalPages > 0) {
          setPagination(prev => ({ ...prev, page: newTotalPages }));
          return; // State update will trigger a new fetch automatically
        }

        setData(listData);
        setPagination(prev => ({
          ...prev,
          total: meta.total || 0,
          totalPages: newTotalPages
        }));

        if (response.warning) {
          showWarning(response.warning);
        }
      } else {
        throw new Error(response.message || 'Veri yüklenemedi');
      }
    } catch (err) {
      console.error(`Failed to fetch data from ${endpoint}:`, err);
      // Auth error is handled globally mostly, but we set local error state
      if (!err.isAuthError) {
        if (err.apiError) {
          showServerError();
        } else {
          showError(err.message || 'Veri yüklenirken bir hata oluştu');
        }
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildQueryString]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };

  const handleSortChange = (field) => {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    data,
    loading,
    error,
    pagination,
    filters,
    sort,
    handlePageChange,
    handleLimitChange,
    handleFilterChange,
    handleSortChange,
    refresh,
    setFilters // Expose for complex operations like "Clear All"
  };
};
